import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useChartTheme } from "../../../utils/chartTheme";

ChartJS.register(ArcElement, BarElement, CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip);

/* =============================================================
   An AI Board result as a chart. The API decided the chart and worked
   its data out of the rows (aiboard.chart.js): { type, labels,
   datasets: [{ name, values }], note }. This draws it, and nothing
   else -- no figure here is computed.

   Drawn in the house chart theme (utils/chartTheme.js), as the
   classroom and CRM charts are: series colours by slot from the
   palette (slot 1 the accent, assigned by series in the order the API
   gives -- never by rank), hairline grid, ink for every piece of text,
   the house tooltip. A legend whenever there is more than one series
   or a whole split into parts; none for a single series, which the
   result's title names. "Other" is grey: it is the remainder, not a
   series of its own.
   ============================================================= */

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
const trim = (label, max) => {
  const s = String(label ?? "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
};
const words = (key) =>
  String(key)
    .replace(/[_.]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const AiBoardChart = ({ chart, title }) => {
  const theme = useChartTheme();
  const { type, labels, datasets } = chart;
  const isWhole = type === "pie" || type === "donut";
  const isLine = type === "line" || type === "area";
  const horizontal = type === "hbar";
  const stacked = type === "stacked";

  // By slot, never repeated: the API sends at most four series (the
  // theme's validated count) and folds the rest into "Other".
  const colourOf = (name, i) => (name === "Other" ? theme.ink.faint : theme.series[i] || theme.ink.faint);

  const data = useMemo(() => {
    if (isWhole) {
      const values = datasets[0]?.values || [];
      return {
        labels,
        datasets: [
          {
            label: words(datasets[0]?.name || title),
            data: values,
            backgroundColor: labels.map((l, i) => colourOf(l, i)),
            borderColor: theme.surface,
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      };
    }
    return {
      labels,
      datasets: datasets.map((d, i) => {
        const colour = colourOf(d.name, i);
        const label = words(d.name);
        if (isLine) {
          return {
            label,
            data: d.values,
            borderColor: colour,
            backgroundColor: type === "area" && datasets.length === 1 ? theme.wash(colour, 0.12) : colour,
            fill: type === "area" && datasets.length === 1,
            borderWidth: 2,
            pointRadius: labels.length > 40 ? 0 : 3,
            pointHoverRadius: 5,
            pointBackgroundColor: colour,
            pointBorderColor: theme.surface,
            pointBorderWidth: 2,
            tension: 0.25,
          };
        }
        return {
          label,
          data: d.values,
          backgroundColor: colour,
          hoverBackgroundColor: colour,
          // Rounded at the value, square at the baseline; a 2px gap of
          // the surface between stacked parts.
          borderRadius: stacked ? 0 : 4,
          borderSkipped: "start",
          borderColor: theme.surface,
          borderWidth: stacked ? { top: 2 } : 0,
          maxBarThickness: 28,
        };
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, theme, title]);

  const showLegend = isWhole || datasets.length > 1;

  const options = useMemo(() => {
    const valueAxis = theme.valueAxis({ beginAtZero: true, stacked, ticks: { callback: (v) => fmt(v) } });
    const categoryAxis = theme.categoryAxis({
      stacked,
      ticks: {
        autoSkip: !horizontal,
        maxRotation: 0,
        callback(value) {
          return trim(this.getLabelForValue(value), horizontal ? 26 : 12);
        },
      },
    });
    const base = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: "bottom",
          labels: theme.legendLabels(),
        },
        tooltip: {
          ...theme.tooltip,
          displayColors: showLegend,
          callbacks: {
            label: (ctx) => {
              const value = isWhole ? ctx.parsed : horizontal ? ctx.parsed.x : ctx.parsed.y;
              return ` ${showLegend ? `${isWhole ? ctx.label : ctx.dataset.label}: ` : ""}${fmt(value)}`;
            },
          },
        },
      },
    };
    if (isWhole) return { ...base, cutout: type === "donut" ? "62%" : 0 };
    return {
      ...base,
      indexAxis: horizontal ? "y" : "x",
      interaction: isLine ? { mode: "index", intersect: false } : { mode: "nearest", intersect: true },
      scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
    };
  }, [theme, stacked, horizontal, isWhole, isLine, showLegend, type]);

  // Tall enough for every bar on a horizontal chart; the others a fixed panel.
  const height = horizontal ? Math.max(220, labels.length * 28 + 70) : isWhole ? 280 : 300;

  // What the chart says, for a screen reader: the table has the rest.
  const summary = isWhole
    ? labels.map((l, i) => `${l} ${fmt(datasets[0]?.values[i])}`).join(", ")
    : datasets.map((d) => `${words(d.name)}: ${labels.slice(0, 12).map((l, i) => `${l} ${fmt(d.values[i])}`).join(", ")}`).join("; ");

  const Chart = isWhole ? Doughnut : isLine ? Line : Bar;
  return (
    <div className="aib-chart" role="img" aria-label={`${title}. ${summary}`} style={{ height: `${height}px` }}>
      <Chart data={data} options={options} />
    </div>
  );
};

export default AiBoardChart;
